import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import useMultiLanguage from "../../../hook/useMultiLanguage";
import { TablePagination } from "../table-custom/TablePagination";
import { handlePagesChange, handleRowsPerPageChange } from "../../utils/pageUtils";
import { KTSVG } from "../../../../_metronic/helpers";
import { DEFAULT_PAGE_INDEX, ROWS_FOR_PAGE, TYPE, VARIABLE_STRING } from "../../constants/moduleConsts";
import TableHeader from "./header/TableHeader";
import TableBodyRow from "./body/TableBodyRow";
import "./table-grouping.scss";
import { filterChild } from "./utils/tableGroupingUtils";
import RowTotal from "./body/RowTotal";
import { GroupButton } from "../GroupButton";
import { Form } from "react-bootstrap";

export interface IColumnsGrouping {
  name: string;
  field: string;
  child?: IColumnsGrouping[];
  headerStyle?: React.CSSProperties;
  className?: string;
  cellClassName?: (row: any, index: number) => string;
  cellStyle?: React.CSSProperties;
  render?: (row: any, STT: number, index: number, data: any) => string | number | React.ReactNode;
  type?: TYPE.MONEY | TYPE.NUMBER_FLOAT | TYPE.DATE;
  numberFixed?: number;
  isSticky?: boolean;
}

export interface IColumnsTotal {
  field: string;
  colSpan?: number;
  cellStyle?: React.CSSProperties;
  render?: (row: any) => string | number | React.ReactNode;
  isTitle?: boolean;
  type?: TYPE.MONEY | TYPE.NUMBER_FLOAT;
  numberFixed?: number;
}

interface IProps {
  columns: IColumnsGrouping[];
  data: any[];
  totalPages?: number;
  totalElements?: number;
  numberOfElements?: number;
  buttonAdd?: boolean;
  buttonExportExcel?: boolean;
  handleExportExcel?: (row: any) => void;
  handleOpenDialog?: (row: any) => void;
  title?: string;
  noPagination?: boolean;
  page?: number;
  handleDoubleClick?: (row: any) => void;
  className?: string;
  showTotalRow?: boolean;
  columnsTotal?: IColumnsTotal[];
  id: string;
  prefixTitleField?: string;
  prefixDataField?: string;
  searchObject?: any;
  setSearchObject?: (row: any) => void;
  isSelect?: boolean;
  uniquePrefix?: string;
  dataChecked?: any[];
  setDataChecked?: (dataChecked: any) => void;
}

function TableGrouping(props: IProps) {
  const {
    columns,
    data,
    totalElements,
    totalPages,
    numberOfElements,
    buttonAdd,
    buttonExportExcel,
    handleExportExcel,
    handleOpenDialog,
    title,
    noPagination = true,
    handleDoubleClick,
    className,
    showTotalRow = false,
    columnsTotal,
    id,
    prefixTitleField = "title",
    prefixDataField = "data",
    searchObject,
    setSearchObject = () => { },
    uniquePrefix = "id",
    isSelect = false,
    dataChecked = [],
    setDataChecked = () => { },
  } = props;

  const { lang } = useMultiLanguage();

  const checkBox = isSelect
    ? [
      {
        name: "select",
        field: "",
        render: (
          rowData: any,
          index: number,
          numericalOrder: number,
          itemList: any
        ) => (
          <Form.Check
            className="checkBox"
            checked={rowData?.isChecked || false}
            onChange={(event: ChangeEvent<HTMLInputElement>) =>
              handleCheckBox(event?.target?.checked, index)
            }
          />
        )
      }
    ]
    : [];

  const [itemList, setItemList] = useState<any>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState<number>(props?.page || DEFAULT_PAGE_INDEX);
  const [selectedRows, setSelectedRows] = useState<any[]>(dataChecked || []);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);

  useEffect(() => {
    setItemList(data);
  }, [data])

  useEffect(() => {
    if (itemList?.length > 0) {
      checkedAll(itemList);
    }
  }, [itemList]);

  
  useEffect(() => {
    if (dataChecked) {
      setSelectedRows(dataChecked);
    }
  }, [dataChecked]);

  const handleChangeSearchObject = (name: string, value: any) => {
    switch (name) {
      case VARIABLE_STRING.PAGE_SIZE: {
        setRowsPerPage(value);
        setSearchObject({
          ...searchObject,
          [name]: value,
          [VARIABLE_STRING.PAGE_INDEX]: 1
        })
        break;
      }
      default:
        setPage(value);
        setSearchObject({
          ...searchObject,
          [name]: value,
        })
    }
  }

  const columnsConvert: IColumnsGrouping[] = useMemo(() => {
    return filterChild(columns).flat(Infinity);
  }, [columns]);

  const handleCheckBox = useCallback((checked: boolean, index: number) => {
    let _listData = [...itemList]
    _listData[index].isChecked = checked;
    setItemList([..._listData]);
    let updatedSelectedRows = [...selectedRows];
    const selectedItem = _listData[index];
    if (checked) {
      updatedSelectedRows.push(selectedItem);
    } else {
      updatedSelectedRows = updatedSelectedRows.filter(
        (item: any) => item?.[uniquePrefix] !== selectedItem?.[uniquePrefix]
      );
    }
    setSelectedRows(updatedSelectedRows);
    setDataChecked && setDataChecked(updatedSelectedRows);
  }, [itemList]);

  const handleCheckBoxAll = useCallback((checked: boolean) => {
    const updatedItemList = itemList.map((element: any) => ({
      ...element,
      isChecked: checked,
    }));

    setItemList(updatedItemList);
  
    let updatedSelectedRows = [...selectedRows];
  
    updatedItemList.forEach((element: any) => {
      const index = updatedSelectedRows.findIndex(
        (item: any) => item?.[uniquePrefix] === element?.[uniquePrefix]
      );
  
      if (checked && index === -1) {
        updatedSelectedRows.push(element);
      }
  
      if (!checked && index > -1) {
        updatedSelectedRows.splice(index, 1);
      }
    });
  
    setSelectedRows(updatedSelectedRows);
    setDataChecked && setDataChecked(updatedSelectedRows);
  }, [itemList, selectedRows, uniquePrefix, setDataChecked]);

  const checkedAll = (listData: any[]) => {
    let data = listData.filter((item: any) => item?.isChecked);
    setIsCheckAll(data?.length === 0 ? false : (data?.length === listData?.length));
  };

  return (
    <div className={className || ""}>
      {(buttonAdd || buttonExportExcel || title) && (
        <div className="d-flex align-items-center flex-space-between spaces mb-10">
          {title && <span className="spaces fs-18 text-header-table fw-600">{title}</span>}
          <div className="spaces p-0 d-flex align-items-center">
            {buttonAdd && (
              <GroupButton className="spaces mx-16" handleEvent={handleOpenDialog}>
                <i className="spaces bi bi-plus fs-20 white"></i>
                <p className="spaces fs-14 m-0 ">{lang("BTN.ADD")}</p>
              </GroupButton>
            )}

            {buttonExportExcel && (
              <GroupButton className="spaces mr-16" outline handleEvent={handleExportExcel}>
                <KTSVG path="/media/icons/export-excel.svg" /> {lang("BTN.EXPORT")}
              </GroupButton>
            )}
          </div>
        </div>
      )}

      <div className={`table-responsive customs-collapse-row m-0 table-group`}>
        <table className="table-row-dashed dataTable table w-100 border-bottom border-b-2">
          <TableHeader
          columns={[...checkBox, ...columns]}
          isCheckAll={isCheckAll} 
          tableId={id}
          data={data}
          handleCheckBoxAll={handleCheckBoxAll}
          />
          <tbody className="bg-white">
            {itemList?.length > 0 ? (
              <>
                {itemList.map((row: any, index: number) => (
                  <TableBodyRow
                    key={index}
                    currentIndex={index}
                    columns={[...checkBox, ...columnsConvert]}
                    rowData={row}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    handleDoubleClick={handleDoubleClick}
                    tableId={id}
                    prefixDataField={prefixDataField}
                    prefixTitleField={prefixTitleField}
                    columnsTotal={columnsTotal}
                    showTotalRow={showTotalRow}
                  />
                ))}
                {columnsTotal && showTotalRow && (
                  <RowTotal
                    data={itemList}
                    columnsTotal={columnsTotal || []}
                    prefixDataField={prefixDataField}
                    prefixTitleField={prefixTitleField}
                  />
                )}
              </>
            ) : (
              <tr>
                <td colSpan={columnsConvert.length} className="text-center border">
                  {lang("TABLE.DATA.EMPTY")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {!noPagination && (
        <TablePagination
          page={page}
          setPage={(pageIndex) => handleChangeSearchObject(VARIABLE_STRING.PAGE_INDEX, pageIndex)}
          handlePagesChange={handlePagesChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          rowsForPage={ROWS_FOR_PAGE}
          rowsPerPage={rowsPerPage}
          setRowsPerPage={(pageSize) => handleChangeSearchObject(VARIABLE_STRING.PAGE_SIZE, pageSize)}
          totalPages={totalPages || 0}
          totalElements={totalElements || 0}
          numberOfElements={numberOfElements || 0}
        />
      )}
    </div>
  );
}

export default TableGrouping;
